import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Storage as StorageIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { collection, getDocs, deleteDoc, doc, query, orderBy, where, updateDoc, limit } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, ChartTooltip, Legend);

const MonthlyGraphAnalysis = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewAllDialogOpen, setPreviewAllDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editCandlesDialogOpen, setEditCandlesDialogOpen] = useState(false);
  const [editingCandlesItem, setEditingCandlesItem] = useState(null);
  const [editingCandlesData, setEditingCandlesData] = useState([]);

  useEffect(() => {
    loadMonthlyData();
  }, []);

  const loadMonthlyData = async () => {
    setLoading(true);
    try {
      // Load from monthly_closings collection to get the actual closure data
      const q = query(collection(DATABASE, "monthly_closings"), orderBy("date", "desc"));
      const querySnapshot = await getDocs(q);
      
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().date?.toDate?.() || new Date(),
        source_collection: "monthly_closings",
        // Map the closure data to match the expected format
        total_Monthly_percantage: doc.data().percent,
        graph_month: doc.data().closing_month,
        graph_year: doc.data().closing_year,
        user_id: doc.data().user_id,
      }));

      // Enrich data with user information
      const enrichedData = await Promise.all(data.map(async (item) => {
        console.log("Processing monthly closure item:", item.id, "Available fields:", Object.keys(item));
        
        // Use the user_id directly from monthly_closings
        const userId = item.user_id;
        console.log("Looking for user with ID:", userId);
        
        if (userId) {
          try {
            // Try using the userId as the document ID first
            let userDoc = await getDocs(query(collection(DATABASE, "users"), where("__name__", "==", userId)));
            
            if (userDoc.empty) {
              // Try other possible field names
              userDoc = await getDocs(query(collection(DATABASE, "users"), where("userID", "==", userId)));
            }
            if (userDoc.empty) {
              userDoc = await getDocs(query(collection(DATABASE, "users"), where("user_id", "==", userId)));
            }
            
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              console.log("Found user data:", userData);
              return {
                ...item,
                userEmail: userData.email || "Unknown Email",
                userName: userData.full_name || userData.first_name || "Unknown User",
              };
            } else {
              console.log("No user found for ID:", userId);
            }
          } catch (error) {
            console.error("Error fetching user data for monthly closure:", error);
          }
        } else {
          console.log("No user ID found in monthly closure item");
        }
        
        return {
          ...item,
          userEmail: "Unknown Email",
          userName: "Unknown User",
        };
      }));
      
      setMonthlyData(enrichedData);
    } catch (error) {
      console.error("Error loading monthly data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load monthly graphs data",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = monthlyData.filter((item) => {
    const emailMatch = !filterEmail || item.userEmail?.toLowerCase().includes(filterEmail.toLowerCase());
    const monthMatch = !filterMonth || 
      (item.graph_month !== undefined && item.graph_year !== undefined && 
       dayjs().month(item.graph_month).year(item.graph_year).format("MMMM YYYY").toLowerCase().includes(filterMonth.toLowerCase()));
    return emailMatch && monthMatch;
  });

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = filteredData.map((item) => item.id);
      setSelectedItems(newSelected);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handlePreviewMonthlyGraph = (item) => {
    setPreviewItem(item);
    setPreviewDialogOpen(true);
  };

  const handlePreviewAllMonthlyGraphs = () => {
    setPreviewAllDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      const updateData = {};
      if (editingItem.total_Monthly_percantage !== undefined && editingItem.total_Monthly_percantage !== null && editingItem.total_Monthly_percantage !== '') {
        updateData.total_Monthly_percantage = parseFloat(editingItem.total_Monthly_percantage) || 0;
      }
      if (editingItem.graph_month !== undefined && editingItem.graph_month !== null && editingItem.graph_month !== '') {
        updateData.graph_month = parseInt(editingItem.graph_month) || 0;
      }
      if (editingItem.graph_year !== undefined && editingItem.graph_year !== null && editingItem.graph_year !== '') {
        updateData.graph_year = parseInt(editingItem.graph_year) || 0;
      }

      // Add updated timestamp
      updateData.updated_at = new Date();

      await updateDoc(doc(DATABASE, "monthly_grpahs", editingItem.id), updateData);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Monthly graph entry updated successfully",
      });

      setEditDialogOpen(false);
      setEditingItem(null);
      loadMonthlyData();
    } catch (error) {
      console.error("Error updating item:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update monthly graph entry",
      });
    }
  };

  const handleEditCandles = (item) => {
    setEditingCandlesItem(item);
    
    // Sort graph points by date for editing
    const sortedPoints = [...(item.graph_points || [])].sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateA - dateB;
    });
    
    setEditingCandlesData(sortedPoints);
    setEditCandlesDialogOpen(true);
  };

  const handleSaveCandles = async () => {
    if (!editingCandlesItem) return;

    try {
      // Calculate new total percentage from the edited candle data
      const totalPercentage = editingCandlesData.reduce((sum, candle) => {
        return sum + (parseFloat(candle.amount) || 0);
      }, 0);

      // Update the graph_points with the edited data and recalculate total percentage
      const updateData = {
        graph_points: editingCandlesData,
        total_Monthly_percantage: totalPercentage, // Recalculate total percentage
        updated_at: new Date(),
      };

      await updateDoc(doc(DATABASE, "monthly_grpahs", editingCandlesItem.id), updateData);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Monthly graph candles updated successfully. New total percentage: ${totalPercentage.toFixed(2)}%`,
      });

      setEditCandlesDialogOpen(false);
      setEditingCandlesItem(null);
      setEditingCandlesData([]);
      loadMonthlyData();
    } catch (error) {
      console.error("Error updating candles:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update monthly graph candles",
      });
    }
  };

  const handleCandleAmountChange = (index, newAmount) => {
    const updatedData = [...editingCandlesData];
    updatedData[index] = {
      ...updatedData[index],
      amount: parseFloat(newAmount) || 0,
    };
    setEditingCandlesData(updatedData);
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select items to delete",
      });
      return;
    }

    setItemsToDelete(selectedItems);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      // Delete from monthly_closings since that's where the data is loaded from
      const deletePromises = itemsToDelete.map((itemId) =>
        deleteDoc(doc(DATABASE, "monthly_closings", itemId))
      );

      await Promise.all(deletePromises);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: `${itemsToDelete.length} items deleted successfully`,
      });

      setSelectedItems([]);
      setDeleteDialogOpen(false);
      loadMonthlyData();
    } catch (error) {
      console.error("Error deleting items:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete selected items",
      });
    }
  };

  const getStatusChip = (item) => {
    // Check for missing required fields for monthly closures
    const missingFields = [];
    
    if (!item.user_id) missingFields.push("UserID");
    if (item.closing_month === undefined || item.closing_month === null) missingFields.push("Closing Month");
    if (item.closing_year === undefined || item.closing_year === null) missingFields.push("Closing Year");
    if (item.percent === undefined || item.percent === null) missingFields.push("Performance %");

    if (missingFields.length > 0) {
      return <Chip label={`Missing: ${missingFields.join(", ")}`} color="error" size="small" />;
    }

    // Check performance type
    if (item.percent < 0) {
      return <Chip label="Loss" color="error" size="small" />;
    } else if (item.percent > 0) {
      return <Chip label="Gain" color="success" size="small" />;
    } else {
      return <Chip label="No Change" color="default" size="small" />;
    }
  };

  const getPerformanceSummary = (item) => {
    if (!item.graph_points || !Array.isArray(item.graph_points)) {
      return "No data";
    }

    const validDays = item.graph_points.filter(day => 
      day.amount !== undefined && 
      day.amount !== null
    );

    if (validDays.length === 0) {
      return "No performance data";
    }

    const totalPerformance = validDays.reduce((sum, day) => 
      sum + (parseFloat(day.amount) || 0), 0
    );

    return `${totalPerformance.toFixed(2)}%`;
  };

  // Generate candlestick chart data for monthly graph preview (exactly like Flutter app)
  const generateMonthlyChartData = (monthlyItem) => {
    if (!monthlyItem || !monthlyItem.graph_points || !Array.isArray(monthlyItem.graph_points)) {
      return null;
    }

    // Sort by date to ensure proper chronological order
    const sortedPoints = [...monthlyItem.graph_points].sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateA - dateB;
    });

    if (sortedPoints.length === 0) {
      return null;
    }

    // Generate labels (dates) for X-axis
    const labels = sortedPoints.map(point => {
      const date = point.date?.toDate ? point.date.toDate() : new Date(point.date);
      return dayjs(date).format("DD");
    });

    // Create candlestick data using open, close, high, low values (like Flutter app)
    const candlestickData = sortedPoints.map(point => {
      // Use the amount field as the main value (like Flutter app's volume)
      const amount = parseFloat(point.amount) || 0;
      
      // For candlestick visualization, we'll use amount as the bar height
      // Green bars for positive values, red/blue for negative values
      return {
        value: Math.abs(amount), // Absolute value for bar height
        isPositive: amount >= 0, // Color determination
        originalAmount: amount, // Keep original for tooltip
      };
    });

    const data = {
      labels: labels,
      datasets: [
        {
          label: `Daily Performance`,
          data: candlestickData.map(item => item.value),
          backgroundColor: candlestickData.map(item => 
            item.isPositive ? '#00AD3C' : '#FF6B6B' // Green for positive, red for negative
          ),
          borderColor: candlestickData.map(item => 
            item.isPositive ? '#00AD3C' : '#FF6B6B'
          ),
          borderWidth: 1,
          barThickness: 15, // Make bars thinner like candlesticks
          maxBarThickness: 20,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // Hide legend for cleaner look
        },
        tooltip: {
          callbacks: {
            label: function (tooltipItem) {
              const dayIndex = tooltipItem.dataIndex;
              const originalAmount = candlestickData[dayIndex].originalAmount;
              const date = sortedPoints[dayIndex].date?.toDate ? 
                sortedPoints[dayIndex].date.toDate() : 
                new Date(sortedPoints[dayIndex].date);
              
              return [
                `${dayjs(date).format("DD/MM/YYYY")}`,
                `Performance: ${originalAmount.toFixed(2)}%`
              ];
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Days of Month',
            font: {
              size: 12,
              weight: 'bold'
            }
          },
          ticks: {
            font: {
              size: 10
            }
          }
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Daily Performance %',
            font: {
              size: 12,
              weight: 'bold'
            }
          },
          ticks: {
            callback: function (value) {
              return `${value.toFixed(2)}%`;
            },
            font: {
              size: 10
            }
          },
        },
      },
    };

    return { data, options };
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Monthly Closure Data Analysis
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Analyze and manage monthly closure data, view actual performance percentages, and validate closure calculations.
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {monthlyData.length}
              </Typography>
              <Typography variant="body2">Total Entries</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {monthlyData.filter(item => 
                  item.user_id && 
                  item.closing_month !== undefined && 
                  item.closing_year !== undefined &&
                  item.percent !== undefined
                ).length}
              </Typography>
              <Typography variant="body2">Valid Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {monthlyData.filter(item => item.percent < 0).length}
              </Typography>
              <Typography variant="body2">Loss Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {monthlyData.filter(item => 
                  !item.user_id || item.closing_month === undefined || item.percent === undefined
                ).length}
              </Typography>
              <Typography variant="body2">Invalid Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Filter by Email"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Filter by Month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<VisibilityIcon />}
          onClick={handlePreviewAllMonthlyGraphs}
          disabled={loading}
        >
          Preview Global Monthly Graph
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadMonthlyData}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteSelected}
          disabled={selectedItems.length === 0}
        >
          Delete Selected ({selectedItems.length})
        </Button>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedItems.length > 0 && selectedItems.length < filteredData.length}
                  checked={filteredData.length > 0 && selectedItems.length === filteredData.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>User Email</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Performance %</TableCell>
              <TableCell>Profit/Loss</TableCell>
              <TableCell>Closing Balance</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {item.userEmail || "Unknown Email"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.userName || "Unknown User"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.closing_month !== undefined && item.closing_year !== undefined 
                      ? dayjs().month(item.closing_month).year(item.closing_year).format("MMMM YYYY")
                      : "N/A"
                    }
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={item.percent < 0 ? "error.main" : item.percent > 0 ? "success.main" : "text.secondary"}
                    >
                      {item.percent !== undefined 
                        ? `${item.percent.toFixed(2)}%`
                        : "N/A"
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={item.profit_loss < 0 ? "error.main" : "success.main"}
                      fontWeight="bold"
                    >
                      ${item.profit_loss?.toFixed(2) || "0.00"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      ${item.closing_balance?.toFixed(2) || "0.00"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
                  </TableCell>
                  <TableCell>{getStatusChip(item)}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit Summary">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditItem(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Individual Candles">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleEditCandles(item)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Preview Monthly Graph">
                      <IconButton 
                        size="small"
                        color="info"
                        onClick={() => handlePreviewMonthlyGraph(item)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setItemsToDelete([item.id]);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {itemsToDelete.length} selected item(s)?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Monthly Graph Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Monthly Graph Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This is how the monthly graph appears to users. The graph shows the day-to-day fund performance for the selected month.
          </Typography>
          {previewItem && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>Graph Details:</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Month:</strong> {previewItem.graph_month !== undefined && previewItem.graph_year !== undefined ? dayjs().month(previewItem.graph_month).year(previewItem.graph_year).format("MMMM YYYY") : "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Total Percentage:</strong> {previewItem.total_Monthly_percantage !== undefined ? `${previewItem.total_Monthly_percantage.toFixed(2)}%` : "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Days Count:</strong> {previewItem.graph_points?.length || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Performance:</strong> {getPerformanceSummary(previewItem)}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Box sx={{ height: 400, border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
            {(() => {
              const chartData = generateMonthlyChartData(previewItem);
              if (chartData) {
                return (
                  <Box sx={{ width: '100%', height: '350px' }}>
                    <Bar data={chartData.data} options={chartData.options} />
                  </Box>
                );
              } else {
                return (
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No monthly graph data available to display
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Please ensure monthly graph data is properly loaded and contains valid performance data
                    </Typography>
                  </Box>
                );
              }
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Monthly Graph Entry</DialogTitle>
        <DialogContent>
          {editingItem && (
            <Box sx={{ mt: 2 }}>
              <TextField 
                fullWidth 
                label="Total Monthly Percentage" 
                type="number" 
                value={editingItem.total_Monthly_percantage || ''} 
                onChange={(e) => setEditingItem({ ...editingItem, total_Monthly_percantage: parseFloat(e.target.value) || 0 })} 
                margin="normal" 
              />
              <TextField 
                fullWidth 
                label="Graph Month (0-11)" 
                type="number" 
                value={editingItem.graph_month || ''} 
                onChange={(e) => setEditingItem({ ...editingItem, graph_month: parseInt(e.target.value) || 0 })} 
                margin="normal" 
              />
              <TextField 
                fullWidth 
                label="Graph Year" 
                type="number" 
                value={editingItem.graph_year || ''} 
                onChange={(e) => setEditingItem({ ...editingItem, graph_year: parseInt(e.target.value) || 0 })} 
                margin="normal" 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Preview All Monthly Graphs Dialog */}
      <Dialog open={previewAllDialogOpen} onClose={() => setPreviewAllDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>All Monthly Graphs Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This shows all monthly graphs data. The graphs represent the day-to-day fund performance for each month.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Summary:</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="body2"><strong>Total Entries:</strong> {monthlyData.length}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2"><strong>Complete Entries:</strong> {monthlyData.filter(item => item.graph_points?.length > 0).length}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2"><strong>Incomplete Entries:</strong> {monthlyData.filter(item => !item.graph_points?.length).length}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2"><strong>Invalid Entries:</strong> {monthlyData.filter(item => !item.total_Monthly_percantage).length}</Typography>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ height: 400, border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
            {(() => {
              // Find the most recent monthly graph with data
              const latestGraphWithData = monthlyData
                .filter(item => item.graph_points && item.graph_points.length > 0)
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

              if (latestGraphWithData) {
                const chartData = generateMonthlyChartData(latestGraphWithData);
                if (chartData) {
                  return (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Latest Monthly Graph: {dayjs().month(latestGraphWithData.graph_month).year(latestGraphWithData.graph_year).format("MMMM YYYY")}
                      </Typography>
                      <Box sx={{ width: '100%', height: '320px' }}>
                        <Bar data={chartData.data} options={chartData.options} />
                      </Box>
                    </Box>
                  );
                }
              }

              return (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No monthly graph data available to display
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    Total Data Points: {monthlyData.reduce((total, item) => total + (item.graph_points?.length || 0), 0)} across {monthlyData.length} months
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Please ensure monthly graph data is properly loaded and contains valid performance data
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewAllDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Individual Candles Dialog */}
      <Dialog open={editCandlesDialogOpen} onClose={() => setEditCandlesDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Individual Daily Candles</DialogTitle>
        <DialogContent>
          {editingCandlesItem && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {editingCandlesItem.userEmail} - {editingCandlesItem.userName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Month: {editingCandlesItem.graph_month !== undefined && editingCandlesItem.graph_year !== undefined 
                  ? dayjs().month(editingCandlesItem.graph_month).year(editingCandlesItem.graph_year).format("MMMM YYYY")
                  : "N/A"
                }
              </Typography>
              
              {/* Live Total Calculation */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  Live Total Calculation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Current Total:
                    </Typography>
                    <Typography variant="h5" color={(() => {
                      const total = editingCandlesData.reduce((sum, candle) => sum + (parseFloat(candle.amount) || 0), 0);
                      return total >= 0 ? 'success.main' : 'error.main';
                    })()}>
                      {editingCandlesData.reduce((sum, candle) => sum + (parseFloat(candle.amount) || 0), 0).toFixed(2)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Original Total:
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {editingCandlesItem.total_Monthly_percantage?.toFixed(2) || '0.00'}%
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Changes are calculated in real-time as you edit the daily values above
                </Typography>
              </Box>
            </Box>
          )}
          
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Performance %</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editingCandlesData.map((candle, index) => {
                    const date = candle.date?.toDate ? candle.date.toDate() : new Date(candle.date);
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            Day {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {dayjs(date).format("DD/MM/YYYY")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={candle.amount || 0}
                            onChange={(e) => handleCandleAmountChange(index, e.target.value)}
                            inputProps={{
                              step: 0.01,
                              min: -100,
                              max: 100,
                            }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={candle.amount >= 0 ? "Gain" : "Loss"}
                            color={candle.amount >= 0 ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  
                  {/* Summary Row */}
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" fontWeight="bold">
                        TOTAL
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color={(() => {
                        const total = editingCandlesData.reduce((sum, candle) => sum + (parseFloat(candle.amount) || 0), 0);
                        return total >= 0 ? 'success.main' : 'error.main';
                      })()}>
                        {editingCandlesData.reduce((sum, candle) => sum + (parseFloat(candle.amount) || 0), 0).toFixed(2)}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(() => {
                          const total = editingCandlesData.reduce((sum, candle) => sum + (parseFloat(candle.amount) || 0), 0);
                          return total >= 0 ? "Net Gain" : "Net Loss";
                        })()}
                        color={(() => {
                          const total = editingCandlesData.reduce((sum, candle) => sum + (parseFloat(candle.amount) || 0), 0);
                          return total >= 0 ? "success" : "error";
                        })()}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCandlesDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCandles} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyGraphAnalysis;
